import { languageNames, supportedLanguages, type SupportedLanguage } from "./resources";

const primaryNameTags = <T extends Partial<Record<SupportedLanguage, string>>>(
  tags: T,
): Partial<Record<SupportedLanguage, string>> => tags;

/** BCP-47 base language tag (drop region and everything after), e.g. "ja-JP" → "ja". */
const baseLanguage = (code: string): string => code.split("-")[0];

/** Uppercase the first character (locale-aware). */
const capitalizeFirst = (value: string, locale: string): string =>
  value.length > 0 ? value.charAt(0).toLocaleUpperCase(locale) + value.slice(1) : value;

/**
 * Which subtag to feed Intl.DisplayNames for the localized primary name. Most locales use their
 * bare base subtag so the name stays clean and region-free ("Danish", "Korean", "Japanese").
 *
 * A few locales intentionally carry a regional/script qualifier in the product label, so they map
 * to a fuller tag that Intl (with languageDisplay: "standard") renders with the qualifier:
 *   - pt-BR/fr-FR/fr-CA/es-419/es-ES → the region tag → "Portuguese (Brazil)", "French (France)", …
 *   - zh-CN/zh-TW → the SCRIPT tags Hans/Hant, because Intl renders the region tags as
 *     "Chinese (China)"/"Chinese (Taiwan)" rather than the Simplified/Traditional distinction the
 *     product uses ("Chinese (Simplified)"/"Chinese (Traditional)").
 */
const PRIMARY_NAME_TAG = primaryNameTags({
  "es-419": "es-419",
  "es-ES": "es-ES",
  "pt-BR": "pt-BR",
  "fr-CA": "fr-CA",
  "fr-FR": "fr-FR",
  "zh-CN": "zh-Hans",
  "zh-TW": "zh-Hant",
});

/**
 * Returns the language name in the viewer's locale. For most locales this uses only the base
 * language subtag (e.g. "ja" rather than "ja-JP") so Intl.DisplayNames returns a clean, region-free
 * name like "Japanese". Locales in PRIMARY_NAME_TAG instead resolve a region/script tag with
 * languageDisplay "standard" so the qualifier is kept (e.g. "Portuguese (Brazil)",
 * "Chinese (Simplified)").
 *
 * The first letter is capitalized because some locales (e.g. French, Spanish, Italian, Portuguese)
 * lowercase language names by convention (`fr` → "français"), which reads wrong as a standalone
 * UI label.
 *
 * Returns undefined if Intl cannot resolve the code or if it throws.
 */
const primaryName = (code: SupportedLanguage, viewerLocale: string): string | undefined => {
  try {
    const dn = new Intl.DisplayNames([viewerLocale], {
      type: "language",
      languageDisplay: "standard",
    });
    const result = dn.of(PRIMARY_NAME_TAG[code] ?? baseLanguage(code));
    return result ? capitalizeFirst(result, viewerLocale) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Label for a locale option: the language name in the viewer's locale, with the
 * curated native name (endonym) from `languageNames` in parentheses when they
 * differ. Collapses to the curated endonym alone when:
 *   - code is not in languageNames or Intl cannot resolve it (fallback),
 *   - the viewer's base language matches the code's base language (own-language
 *     collapse), or
 *   - the resolved primary name equals the endonym.
 *
 * The result never contains a flag emoji; the `languageNames` map is already
 * de-emoji'd and this function does not add any.
 *
 * @param code - The locale to describe (must be a SupportedLanguage).
 * @param viewerLocale - The locale of the person reading the label (BCP-47). Defaults to 'en-US'.
 */
export const formatLocaleLabel = (
  code: SupportedLanguage,
  viewerLocale: string = "en-US",
): string => {
  const endonym = languageNames[code];

  // Rule 1: code not in languageNames OR Intl cannot resolve it → fallback.
  if (endonym === undefined) {
    return code;
  }

  const primary = primaryName(code, viewerLocale);

  // Rule 1 (continued): Intl can't resolve → return curated endonym.
  if (primary === undefined) {
    return endonym;
  }

  // Rule 2: viewer's base language matches code's base language → collapse.
  if (baseLanguage(code) === baseLanguage(viewerLocale)) {
    return endonym;
  }

  // Rule 3: primary equals endonym → collapse.
  if (primary === endonym) {
    return endonym;
  }

  // Rule 4: show "Primary (endonym)".
  return `${primary} (${endonym})`;
};

/**
 * All supported locale codes with their viewer-localised labels,
 * in the order defined by `supportedLanguages` (not sorted alphabetically).
 * Useful for building locale selector options.
 */
export const allLocaleLabels = (viewerLocale: string = "en-US") =>
  supportedLanguages.map((code) => ({ code, label: formatLocaleLabel(code, viewerLocale) }));
